import { Validators, ValidatorFn } from '@angular/forms';
import { SingleTermAward } from '@k12/models';
import type { RecordSection, FieldOption } from '@k12/app-components';
// Imported by path rather than through @k12/app-components — forms/index is deliberately kept out
// of the package barrel because of its circular dependency on task-forms.
import { lea } from '../../forms/basic-info-const';

/** Rejects negative currency entries. Blank is left alone — these amounts are optional. */
const nonNegativeAmount: ValidatorFn = Validators.min(0);


export const ESA_SECTIONS: RecordSection[] = [
  {
    title: 'Application Information',
    fields: [
      { key: 'iepVerified', label: 'Documentation of Disability', type: 'boolean' },
      { key: 'disabilityType1Id', label: 'Primary Disability', type: 'select', optionsKey: 'disabilityTypes' },
      { key: 'disabilityType2Id', label: 'Secondary Disability', type: 'select', optionsKey: 'disabilityTypes' },
      { key: 'iepVerifiedDate', label: 'Evaluation Date', type: 'date' },
      { key: 'continuingEligibilityType', label: 'Continuing Eligibility Type', type: 'text' },
      {
        key: 'esaPublicSchoolDistrict',
        label: 'Public School District',
        type: 'select',
        optionsKey: 'publicSchoolDistricts'
      },
      { key: 'uidNumber', label: 'UID Number', type: 'text' },
      {
        key: 'esaIneligibilityReasonId',
        label: 'Ineligibility Reason',
        type: 'select',
        optionsKey: 'inEligibilityReasons',
        editableBy: ['admin']
      },
      { key: 'isExitedFromEsaPlus', label: 'Exit Student', type: 'boolean', editableBy: ['admin'] },
      // Only meaningful once the student has been exited, so it stays hidden until the flag is set.
      // The API clears the stored date whenever the flag comes back false.
      {
        key: 'esaExitDate',
        label: 'Exit Date',
        type: 'date',
        editableBy: ['admin'],
        showWhen: { field: 'isExitedFromEsaPlus', is: true }
      }
    ]
  },
  {
    title: 'Scholarship Information',
    fields: [
      // Assigned by the ESA+ lottery, so it is displayed but never written back on save.
      { key: 'esaLotteryNumber', label: 'Lottery Number', type: 'number', readonly: true },
      { key: 'esaAwardAmountOffered', label: 'Total ESA+ Award', type: 'currency'},
      // Display-only: classified from the primary/secondary disability, here and again on the API
      // side. An admin changes it by changing the disability, never by picking a level.
      { key: 'esaFundingLevel', label: 'Award Level', type: 'text', readonly: true },
      // Null for the standard full-year award; Fall or Spring once the award is converted to a
      // single term.
      { key: 'esaSingleTermAward', label: 'Single Term Award', type: 'select', optionsKey: 'singleTermAwards' },
      { key: 'fallESAAwardAmount', label: 'Fall Semester Award Amount', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'springESAAwardAmount', label: 'Spring Semester Award Amount', type: 'currency', validators: [nonNegativeAmount] },
      {
        key: 'endorsedByParentFall',
        label: 'Fall Term Endorsed by Parent',
        type: 'boolean',
        validators: [Validators.required]
      },
      {
        key: 'endorsedByParentSpring',
        label: 'Spring Term Endorsed by Parent',
        type: 'boolean',
        validators: [Validators.required]
      }
    ]
  },
  {
    title: 'Financial Details',
    fields: [
      { key: 'esaFallTuitionAmount', label: 'Fall Tuition to be Paid', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'isESAFallFundProcessed', label: 'Fall Funds Sent to School', type: 'boolean' },
      { key: 'esaSpringTuitionAmount', label: 'Spring Tuition to be Paid', type: 'currency' },
      { key: 'isESASpringFundProcessed', label: 'Spring Funds Sent to School', type: 'boolean' },
      { key: 'esaHoldPayment', label: 'Hold Payment', type: 'boolean', editableBy: ['admin'] },
      { key: 'esaFallDisbursedAmount', label: 'Fall Disbursed Amount', type: 'currency', readonly: true },
      { key: 'esaSpringDisbursedAmount', label: 'Spring Disbursed Amount', type: 'currency', readonly: true }
    ]
  },
  {
    title: 'ClassWallet Information',
    fields: [
      // Only applies once ESA+ funds reach the wallet, so it stays hidden until an amount lands.
      {
        key: 'cwFallFunds',
        label: 'Fall Funds to be Paid',
        type: 'currency',
        showWhen: { field: 'cwFallFunds', is: 'not-null' },
        validators: [nonNegativeAmount]
      },
      {
        key: 'cwSpringFunds',
        label: 'Spring Funds to be Paid',
        type: 'currency',
        validators: [nonNegativeAmount],
        showWhen: { field: 'cwSpringFunds', is: 'not-null' }
      },
      { key: 'cwFundsPaid', label: 'Class Wallet Fund Paid', type: 'currency', readonly: true, fractionDigits: 2 },
      { key: 'cwFundsSpent', label: 'Wallet Funds Spent', type: 'currency', readonly: true, fractionDigits: 2 },
      { key: 'cwFundsRemaining', label: 'Wallet Balance', type: 'currency', readonly: true, fractionDigits: 2 },
      // Derived by the API from holdFundsInClassWallet, so it is displayed rather than edited —
      // admins change availability through the hold flag below.
      { key: 'walletStatus', label: 'Wallet Status', type: 'text', readonly: true },
      { key: 'w9FormReceived', label: 'W9 Form Received', type: 'boolean' },
      { key: 'holdClassWalletFunds', label: 'Hold Class Wallet Funds', type: 'boolean' },
      { key: 'holdFundsInClassWallet', label: 'Hold Funds in Class Wallet', type: 'boolean' }
    ]
  }
];

/**
 * The NC public school units from the shared LEA list, in the order that list declares them.
 * The option value is the district *name* rather than the LEA id, so the column keeps holding the
 * same kind of string it held while this field was free text — the printed application and the
 * ESA+ application projection both read it as a plain name and are unaffected.
 */
const PUBLIC_SCHOOL_DISTRICT_OPTIONS: FieldOption[] = [
  { label: 'None', value: null },
  ...lea.map(({ leaName }) => ({ label: leaName, value: leaName }))
];

/**
 * District options for a record that predates the dropdown. Anything could have been typed into the
 * free-text field, and cfi-record-view renders a select value with no matching option as — while
 * cfi-record-edit would drop it on the next save, so an unrecognised value is offered as its own
 * option until an admin picks a real district.
 */
export function publicSchoolDistrictOptions(current?: string | null): FieldOption[] {
  const value = current?.trim();
  if (!value || PUBLIC_SCHOOL_DISTRICT_OPTIONS.some((option) => option.value === value)) {
    return PUBLIC_SCHOOL_DISTRICT_OPTIONS;
  }
  return [...PUBLIC_SCHOOL_DISTRICT_OPTIONS, { label: value, value }];
}

export const ESA_OPTIONS: Record<string, FieldOption[]> = {
  yesNo: [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ],
  disabilityTypes: [],
  publicSchoolDistricts: PUBLIC_SCHOOL_DISTRICT_OPTIONS,
  singleTermAwards: [
    { label: 'None', value: null },
    { label: 'Fall', value: SingleTermAward.Fall },
    { label: 'Spring', value: SingleTermAward.Spring }
  ],
  inEligibilityReasons: []
};
